'use client';

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  motion,
  useMotionTemplate,
  useMotionValue,
  animate,
} from 'framer-motion';
import { Navbar } from '../components/layout/navbar';
import { Footer } from '../components/layout/footer';
import { Button } from '../components/ui/button';
import {
  Shield,
  Zap,
  Globe,
  Sparkles,
  ArrowRight,
  KeyRound,
} from 'lucide-react';
import { usePassportSSO, useAuthStore, selectIsAuthenticated } from '../hooks';

// Spotlight effect component
function SpotlightCard({ children, className = '' }) {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  function handleMouseMove({ currentTarget, clientX, clientY }) {
    const { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }

  return (
    <div
      className={`relative group border border-white/10 bg-zinc-900/80 overflow-hidden ${className}`}
      onMouseMove={handleMouseMove}
    >
      <motion.div
        className="pointer-events-none absolute -inset-px opacity-0 transition duration-300 group-hover:opacity-100"
        style={{
          background: useMotionTemplate`
            radial-gradient(
              650px circle at ${mouseX}px ${mouseY}px,
              rgba(139, 92, 246, 0.15),
              transparent 80%
            )
          `,
        }}
      />
      <div className="relative h-full">{children}</div>
    </div>
  );
}

const features = [
  {
    icon: Shield,
    title: 'Secure Access',
    desc: 'Sign in with Google, Twitter, Discord or your wallet.',
  },
  {
    icon: Globe,
    title: 'Universal Identity',
    desc: 'One profile across all Tracverse apps.',
  },
  {
    icon: Zap,
    title: 'Instant SSO',
    desc: 'Sign in once, access everything.',
  },
];

export default function SignInPage() {
  const navigate = useNavigate();

  // Passport SSO
  const { redirectToLogin, isCheckingSession } = usePassportSSO();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const storeUser = useAuthStore((state) => state.user);

  // Redirect if already authenticated (has a valid user in store)
  useEffect(() => {
    if (isCheckingSession) return;

    // Only redirect if user is fully authenticated with a valid user record
    if (isAuthenticated && storeUser?.passportId) {
      // If user is not verified, redirect to complete registration
      if (!storeUser.isVerified) {
        navigate('/profile/complete-registration', { replace: true });
        return;
      }
      // If verified, redirect to profile
      const profileId = storeUser.passportId || storeUser.oid;
      navigate(`/profile/${profileId}`, { replace: true });
    }
  }, [navigate, isAuthenticated, storeUser?.passportId, storeUser?.isVerified, isCheckingSession]);

  return (
    <div className="min-h-screen flex flex-col bg-black text-white selection:bg-violet-500/30 font-sans">
      {/* Dynamic Background */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[url('/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]" />

        {/* Animated Orbs */}
        <motion.div
          animate={{ x: [0, 50, 0], y: [0, -50, 0], scale: [1, 1.2, 1] }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-[-10%] left-[-10%] w-[60%] h-[60%] rounded-full bg-violet-600/10 blur-[150px]"
        />
        <motion.div
          animate={{ x: [0, -50, 0], y: [0, 50, 0], scale: [1, 1.1, 1] }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2,
          }}
          className="absolute bottom-[-10%] right-[-10%] w-[60%] h-[60%] rounded-full bg-cyan-600/10 blur-[150px]"
        />
        <div className="absolute top-[40%] left-[50%] -translate-x-[50%] -translate-y-[50%] w-[40%] h-[40%] rounded-full bg-indigo-500/5 blur-[120px]" />
      </div>

      <Navbar />

      <main className="flex-1 relative z-10 flex flex-col lg:flex-row items-center justify-center container px-4 md:px-6 py-12 gap-12 lg:gap-24">
        {/* Left Side: Welcome Text */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="lg:w-1/2 space-y-8 text-center lg:text-left"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm self-center lg:self-start mx-auto lg:mx-0">
            <div className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </div>
            <span className="text-xs font-medium text-zinc-300">
              Web3 Enabled
            </span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-tight">
            Welcome to the <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-purple-400 to-cyan-400 animate-gradient-x">
              Future of Talk
            </span>
          </h1>

          <p className="text-xl text-zinc-400 max-w-xl mx-auto lg:mx-0 leading-relaxed">
            Connect your wallet to access decentralized discussions, verified
            knowledge assets, and specialized AI tools.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
            {features.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + i * 0.1 }}
                className="p-4 rounded-2xl bg-white/5 border border-white/5 backdrop-blur-sm hover:bg-white/10 hover:border-violet-500/30 transition-all group cursor-default"
              >
                <item.icon className="w-6 h-6 text-violet-400 mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-bold text-white mb-1">{item.title}</h3>
                <p className="text-xs text-zinc-500 leading-normal">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Right Side: Interactive Login Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, x: 50 }}
          animate={{ opacity: 1, scale: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="lg:w-[420px] w-full"
        >
          <SpotlightCard className="rounded-[2.5rem] p-8 md:p-10 backdrop-blur-2xl">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-violet-500/20 to-transparent blur-2xl -mr-10 -mt-10 pointer-events-none" />

            <div className="relative z-10 flex flex-col items-center">
              <div className="w-20 h-20 mb-8 rounded-3xl bg-gradient-to-br from-zinc-800 to-black p-[2px] shadow-2xl shadow-violet-500/20">
                <div className="w-full h-full rounded-[20px] bg-zinc-900 flex items-center justify-center overflow-hidden relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-violet-500/20 to-cyan-500/20 animate-pulse-slow" />
                  <KeyRound className="w-8 h-8 text-white relative z-10" />
                </div>
              </div>

              <h2 className="text-2xl font-bold mb-2">Sign In</h2>
              <p className="text-zinc-500 text-sm text-center mb-8">
                Choose your preferred sign-in method
              </p>

              {/* Passport SSO - Recommended */}
              <div className="w-full mb-4 relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-violet-600 to-cyan-600 rounded-xl blur opacity-30 group-hover:opacity-50 transition duration-500" />
                <Button
                  onClick={redirectToLogin}
                  disabled={isCheckingSession}
                  className="relative w-full h-14 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg shadow-violet-500/25"
                >
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                      <Shield className="w-4 h-4" />
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-bold">Continue with Passport</div>
                      <div className="text-[10px] text-white/70">Google, Twitter, Discord & more</div>
                    </div>
                  </div>
                </Button>
              </div>

              <div className="w-full flex items-center gap-4 text-zinc-600 text-xs my-6">
                <div className="h-px bg-white/10 flex-1" />
                <span>Protected by cryptography</span>
                <div className="h-px bg-white/10 flex-1" />
              </div>

              <div className="space-y-3 w-full">
                <Link to="/learn-more" className="block">
                  <Button
                    variant="ghost"
                    className="w-full justify-between text-zinc-400 hover:text-white hover:bg-white/5 h-12 rounded-xl group/btn"
                  >
                    <div className="flex items-center gap-3">
                      <Sparkles className="w-4 h-4 text-amber-400" />
                      <span>New to Web3?</span>
                    </div>
                    <ArrowRight className="w-4 h-4 opacity-50 -translate-x-2 group-hover/btn:translate-x-0 transition-transform" />
                  </Button>
                </Link>
              </div>
            </div>
          </SpotlightCard>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
}
